import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';

import { DoubleShift } from "../../types/Shift";

interface ShiftState {
  selectedDate: string;
  selectedShift: DoubleShift;
  // Note: the system's latest shift (latest tblShift record), fetched by ShiftLoginBootstrapper.
  // Persisted so the offline complete-shift flow can tell whether the selected shift is the
  // latest without a server call or any reliance on DateTime.now().
  latestShiftDate: string | null;
  latestShift: DoubleShift | null;
  shiftStartTime: string | null; // ISO UTC based date
  shiftEndTime: string | null;
  isLocked: boolean;
  canToggleLock: boolean;
  isPastAutoLockCutOff: boolean | null;
  isShiftEditibilityDataLoading: boolean;
  shiftEditabilityFetched: boolean;
}

const initialState: ShiftState = {
  selectedDate: localStorage.getItem("redux_selectedDate") || DateTime.now().toISODate(),
  selectedShift: (localStorage.getItem("redux_selectedShift") as DoubleShift) || 'DAY',
  latestShiftDate: localStorage.getItem("redux_latestShiftDate"),
  latestShift: (localStorage.getItem("redux_latestShift") as DoubleShift) || null,
  shiftStartTime: null,
  shiftEndTime: null,
  isLocked: false,
  canToggleLock: false,
  isPastAutoLockCutOff: null,
  isShiftEditibilityDataLoading: false,
  shiftEditabilityFetched: false,
};

const selectedShiftSlice = createSlice({
  name: 'selectedShift',
  initialState,
  reducers: {
    setSelectedDate(state, action: PayloadAction<string>) {
      state.selectedDate = action.payload;
      localStorage.setItem("redux_selectedDate", action.payload);
    },
    setSelectedShift(state, action: PayloadAction<DoubleShift>) {
      state.selectedShift = action.payload;
      localStorage.setItem("redux_selectedShift", action.payload);
    },
    setLatestShift(state, action: PayloadAction<{ date: string; shift: DoubleShift }>) {
      state.latestShiftDate = action.payload.date;
      state.latestShift = action.payload.shift;
      localStorage.setItem("redux_latestShiftDate", action.payload.date);
      localStorage.setItem("redux_latestShift", action.payload.shift);
    },
    setShiftBoundaries(state, action: PayloadAction<{ start: string; end: string }>) {
      state.shiftStartTime = action.payload.start;
      state.shiftEndTime = action.payload.end;
    },
    setShiftEditability(state, action: PayloadAction<{ isLocked: boolean; canToggleLock: boolean; isPastAutoLockCutOff: boolean | null }>) {
      state.isLocked = action.payload.isLocked;
      state.canToggleLock = action.payload.canToggleLock;
      state.isPastAutoLockCutOff = action.payload.isPastAutoLockCutOff;
      state.shiftEditabilityFetched = true;
    },
    setShiftEditabilityDataLoading(state, action: PayloadAction<boolean>) {
      state.isShiftEditibilityDataLoading = action.payload;
    }
  },
});

export const {
  setSelectedDate,
  setSelectedShift,
  setLatestShift,
  setShiftBoundaries,
  setShiftEditability,
  setShiftEditabilityDataLoading
} = selectedShiftSlice.actions;
export default selectedShiftSlice.reducer;
